/** @format */
'use client';
import LoadingSpinner from '@/components/ui/client/LoadingSpinner';
import DevexButton, { IButtonOptions } from 'devextreme-react/button';
import React, { ReactNode, SyntheticEvent } from 'react';
import styles from './Button.module.css';

//<editor-fold desc="Component">
// Extends DevExtreme's own Button options (e.g. `type`, `width`, `className`) so existing call sites
// that rely on DevExtreme-flavoured styling props keep working, on top of our custom convenience props.
export interface ButtonProps extends Omit<IButtonOptions, 'onClick' | 'children' | 'render' | 'component'> {
  children?: ReactNode;
  onClick?: (event: SyntheticEvent) => void | null | Promise<void>;
  warning?: boolean;
  error?: boolean;
  primary?: boolean;
  disabled?: boolean;
  active?: boolean;
  hasDecorator?: boolean;
  busy?: boolean;
}

const ButtonRender = (props: ButtonProps, ref) => {
  const { children, onClick, warning, error, primary, active, hasDecorator, busy, disabled, className, ...rest } = props;
  const isDisabled = disabled || busy;
  const handleClick = isDisabled ? undefined : (event: unknown) => onClick?.(event as SyntheticEvent);

  return (
    <DevexButton
      {...rest}
      ref={ref}
      onClick={handleClick}
      disabled={isDisabled}
      className={[styles.button, className].filter(Boolean).join(' ')}
      // DevExtreme's Button only renders configured text/icon options by default; `render` is the
      // supported way to project arbitrary content (icon + label) while keeping native dx behaviour/styling.
      render={() => <span className={styles.content}>{busy ? <LoadingSpinner height={'10px'} /> : children}</span>}
    />
  );
};

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(ButtonRender);

Button.displayName = 'Button';

Button.defaultProps = {
  children: <>Button</>,
  onClick: () => null,
  warning: false,
  error: false,
  disabled: false,
  active: false,
  hasDecorator: false,
  busy: false,
};
//</editor-fold>
export default Button;
